/* eslint-disable class-methods-use-this,no-console,no-alert */
import url from 'url';
import auth0 from 'auth0-js';
import jwtDecode from 'jwt-decode';
import moment from 'moment-timezone';
import debug from './debug';

import pkgJson from '../../package.json';

import history from '../history';

const { config: { rootUri: { host: tld } } } = pkgJson;
const oidcPrefix = `https://www.${tld}`;

const {
  REACT_APP_AUTH0_DOMAIN: domain,
  REACT_APP_AUTH0_CLIENT_ID: clientID,
  REACT_APP_AUTH0_AUDIENCE_URL: audience,
} = process.env;

const log = debug({ src: 'lib/AuthService' });

const normalizeProfile = (profile = {}) => {
  const overrides = Object.keys(profile)
    .filter(key => key.startsWith(oidcPrefix))
    .reduce(
      (acc, key) => ({
        ...acc,
        [key.replace(oidcPrefix, '')]: profile[key],
      }),
      {},
    );

  const oidcData = Object.keys(profile)
    .filter(key => !key.startsWith(oidcPrefix))
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: profile[key],
      }),
      {},
    );

  return {
    ...oidcData,
    ...overrides,
  };
};

export default class AuthService {
  auth0 = new auth0.WebAuth({
    domain,
    clientID,
    redirectUri: `${window.location.origin}/callback`,
    audience,
    responseType: 'token id_token',
    scope: 'openid profile',
  });

  userProfile;

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    // this.isAuthenticated = this.isAuthenticated.bind(this);
    // this.getAccessToken = this.getAccessToken.bind(this);
    // this.getProfile = this.getProfile.bind(this);

    this.scheduleRenewal();
  }

  handleAuthentication({ silent = false } = {}) {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        const uri = AuthService.getReturnUrl();

        this.setSession(authResult);

        if (!silent) {
          history.push(uri);
          AuthService.removeReturnUrl();
        }
      } else if (err) {
        console.log(err);
        log(`Error: ${err.error}. Check the console for further details.`);
        history.replace('/');
      }
    });
  }

  login() {
    this.auth0.authorize();
  }

  signup({ inviteCode }) {
    if (!inviteCode) {
      return this.auth0.authorize();
    }

    return this.auth0.authorize({
      inviteCode,
    });
  }

  logout() {
    // Clear access token and ID token from local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('expires_on');
    localStorage.removeItem('profile');
    localStorage.removeItem('scopes');

    AuthService.saveReturnUrl();

    // Attempt to redirect to a known route and let the auth checks kick the unauthed user to login
    history.replace('/dashboard');
  }

  static saveReturnUrl = (uri = window.location.href) => {
    const urlObj = url.parse(url.format(uri));
    const [protocol, , host] = uri.split('/');
    const returnUrl = url.format(urlObj).replace(`${protocol}//${host}`, '');

    localStorage.setItem('returnUrl', returnUrl);
  };

  static getReturnUrl = () => localStorage.getItem('returnUrl') || '/';

  static removeReturnUrl = () => localStorage.removeItem('returnUrl');

  static getProfile = () => {
    const token = AuthService.getToken();

    if (!token) {
      return {};
    }

    const decoded = jwtDecode(token);

    if (!decoded) {
      return {};
    }

    return normalizeProfile(decoded);
  };

  static setProfile = profile => localStorage.setItem('profile', JSON.stringify(profile));

  static getToken = () => localStorage.getItem('id_token');

  static getTokenExpirationDate(token) {
    if (!token) {
      return moment.unix();
    }

    const decoded = jwtDecode(token);

    if (!decoded.exp) {
      return moment.unix();
    }

    return moment.unix(decoded.exp).toDate();
  }

  static isTokenExpired() {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));

    return new Date().getTime() > expiresAt;
  }

  setSession(authResult) {
    log('💾 Setting session for logged in user.');

    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + new Date().getTime());

    localStorage.setItem('expires_at', expiresAt);
    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);

    // schedule a token renewal
    this.scheduleRenewal();
  }

  renewToken() {
    this.auth0.renewAuth(
      {
        audience,
        redirectUri: `${window.location.origin}/silent.html`,
        usePostMessage: true,
        postMessageDataType: 'auth0:silent-authentication',
      },
      (err, result) => {
        if (err) {
          log(`📛 Could not get a new token using silent authentication (${err.error}).`);
        } else {
          this.setSession(result);
          log(`👍 Successfully renewed auth!`);
        }
      },
    );
  }

  scheduleRenewal() {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    const delay = expiresAt - Date.now();

    const mDate = moment(expiresAt);
    log(`⌛️ Auth set to renew at ${mDate.format()} (${mDate.fromNow()})`);

    if (delay > 0) {
      clearTimeout(this.tokenRenewalTimeout);
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
    }
  }

  static isAuthenticated = () => !AuthService.isTokenExpired();
}



// WEBPACK FOOTER //
// ./src/lib/AuthService.js